import {
  CreateButton,
  DateField,
  ExportButton,
  ListContextProvider,
  SelectColumnsButton,
  TextField,
  NumberField,
  TopToolbar,
  ReferenceField,
  FunctionField,
  BulkDeleteButton,
  DeleteButton,
  useListController,
} from "react-admin";
import { Button, Chip } from "@mui/material";
import { Category as CategoryIcon, HelpOutline } from "@mui/icons-material";
import { Link as RouterLink } from "react-router-dom";
import { ResourceName } from "@/types";
import List from "@/components/ui/react-admin/styledList/StyledList";
import { PageHeader } from "@/components/common/PageHeader";
import { StyledDatagridConfigurable } from "@/components/ui/react-admin/StyledDatagrid";
import { useResourcePermissions } from "@/hooks/useResourcePermissions";
import { HelpArticleListAside } from "./HelpArticleListAside";

interface HelpArticleRecord {
  status?: string;
}

const HelpArticleListActions = ({ loading }: { loading?: boolean }) => {
  const { canCreate, canEdit } = useResourcePermissions(ResourceName.HELP_ARTICLES);
  return (
    <TopToolbar>
      {canEdit && (
        <Button
          component={RouterLink}
          to="/help-categories"
          size="small"
          startIcon={<CategoryIcon />}
          sx={{ textTransform: "none" }}
        >
          Manage categories
        </Button>
      )}
      {canCreate && <CreateButton />}
      <SelectColumnsButton disabled={loading} />
      <ExportButton maxResults={50000} disabled={loading} />
    </TopToolbar>
  );
};

/**
 * Admin management grid for KB articles (Feature 4317 / N3), aligned to the
 * Clients list UX: PageHeader + toolbar, contextual Filters aside, configurable
 * columns, row + bulk delete. The permanent include_drafts filter surfaces
 * drafts (the API honors it only for help-articles editors). Writes are
 * SuperAdmin-only, so create/delete affordances are gated by permissions.
 */
export const HelpArticleList = () => {
  const { canDelete } = useResourcePermissions(ResourceName.HELP_ARTICLES);

  const listContext = useListController({
    resource: ResourceName.HELP_ARTICLES,
    sort: { field: "modified_datetime", order: "DESC" },
    filter: { include_drafts: true },
    perPage: 25,
  });
  const { isPending: loading } = listContext;

  return (
    <ListContextProvider value={listContext}>
      <PageHeader
        customTitle="Knowledge Base"
        hideSectionTitle
        rightContent={<HelpArticleListActions loading={loading} />}
        headerIcon={<HelpOutline sx={{ fontSize: 28, color: "primary.main" }} />}
      />
      <List aside={<HelpArticleListAside />} actions={false} loading={loading}>
        <StyledDatagridConfigurable
          rowClick="edit"
          bulkActionButtons={canDelete ? <BulkDeleteButton mutationMode="pessimistic" /> : false}
        >
          <NumberField source="sort_order" label="Order" textAlign="left" />
          <TextField source="title" />
          <ReferenceField
            source="category_id"
            reference={ResourceName.HELP_CATEGORIES}
            label="Category"
            link={false}
            emptyText="—"
          >
            <TextField source="name" />
          </ReferenceField>
          <FunctionField
            label="Status"
            source="status"
            render={(record: HelpArticleRecord) => (
              <Chip
                size="small"
                label={record?.status}
                color={record?.status === "published" ? "success" : "default"}
              />
            )}
          />
          <DateField source="published_datetime" label="Published" emptyText="—" showTime />
          <DateField source="modified_datetime" label="Updated" emptyText="—" showTime />
          {canDelete && <DeleteButton mutationMode="pessimistic" />}
        </StyledDatagridConfigurable>
      </List>
    </ListContextProvider>
  );
};
